import { useEffect, useState } from "react";
import { useCookies } from "react-cookie";
import { t } from "@/i18n-js/instance";
import {
  CIRCLE_DOMAIN,
  GDPR_COOKIE_NAME,
} from "@circle-react/components/constants";
import { staticPages } from "@circle-react/helpers/urlHelpers";
import { Button } from "@circle-react-uikit/Button";
import { getExpirationDate } from "./helpers";
import { REQUIRED_COUNTRIES } from "./requiredCountries";

const hasExcludedQueryParam = (): boolean => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get("request_host") === "app.circle.so";
};

const excludedPaths = [
  "/sign_up",
  "/communities/new",
  /^\/communities\/\d+\/templates$/,
  /^\/communities\/\d+\/questions$/,
];

const isOnboardingPath = (): boolean => {
  const currentPath = window.location.pathname;
  return excludedPaths.some(pattern => {
    if (typeof pattern === "string") {
      return currentPath === pattern;
    }
    return pattern.test(currentPath);
  });
};

export const GDPRCookieBannerComponent = () => {
  const isInsideMobileWebView: boolean =
    !!window.webview?.isComingFromReactNativeWebview ||
    !!window.webview?.isInsideMobileWebview;
  const [cookies, setCookie] = useCookies([GDPR_COOKIE_NAME]);
  const [shouldHideBanner, setShouldHideBanner] = useState<boolean>(true);
  const [countryCode, setCountryCode] = useState<string | undefined>(
    window.CLOUDFLARE_COUNTRY_CODE,
  );
  const { [GDPR_COOKIE_NAME]: acceptedCookies } = cookies;

  const getCurrentUrlDomain = (): string => {
    const hostname = window.location.hostname;
    // Check if the hostname matches the pattern something.circle.so
    const isSubdomainOfCircle =
      hostname.endsWith(`.circle.so`) && hostname.split(".").length === 3;

    if (isSubdomainOfCircle) {
      return CIRCLE_DOMAIN;
    }

    return hostname;
  };

  const isSecureCookie = (): boolean =>
    ["production", "staging"].includes(window.RAILS_ENV || "");

  const shouldShowBanner = (): boolean =>
    ["production", "staging"].includes(window.RAILS_ENV || "");

  const handleClick = (accepted: boolean): void => {
    setCookie(GDPR_COOKIE_NAME, accepted, {
      expires: getExpirationDate(),
      domain: getCurrentUrlDomain(),
      path: "/",
      sameSite: "none",
      secure: isSecureCookie(),
    });
  };

  const getCountryCode = () => {
    // We are saving a cookie called "cookies_enabled" to validate 3th party cookies are enabled,
    // when that cookie is not present we assume that cookies are disabled and we don't show the
    // cookie banner to the user
    if (!acceptedCookies && !countryCode?.length) {
      fetch("https://cloudflare-quic.com/b/headers")
        .then(res => res.json())
        .then((data: { headers: { "Cf-Ipcountry": string } }) =>
          setCountryCode(data.headers["Cf-Ipcountry"]),
        )
        .catch((error: unknown) => {
          console.error(error);
        });
    }
  };

  const setBannerIfRequiredCountry = () => {
    const countryCodes = Object.keys(REQUIRED_COUNTRIES);
    setShouldHideBanner(!countryCodes.includes(countryCode || ""));
  };

  useEffect(() => {
    getCountryCode();
  });

  useEffect(() => {
    setBannerIfRequiredCountry();
  }, [countryCode]);

  if (
    !document.cookie ||
    shouldHideBanner ||
    acceptedCookies !== undefined ||
    isInsideMobileWebView ||
    hasExcludedQueryParam() ||
    isOnboardingPath()
  ) {
    return null;
  }

  if (!shouldShowBanner()) {
    return null;
  }

  return (
    <div className="border-primary bg-primary fixed bottom-0 z-[999999] w-full gap-4 border p-4 shadow-lg md:bottom-8 md:right-8 md:flex md:w-auto md:max-w-3xl md:rounded-lg">
      <div className="mb-4 leading-4 md:mb-0">
        <p className="text-dark text-sm">
          {t(["gdpr.cookie_banner.description"])}
          <br />
          {t("gdpr.cookie_banner.privacy_policy.prefix")}
          <a
            href={staticPages.privacy_policy()}
            target="_blank"
            rel="noreferrer"
          >
            {t("gdpr.cookie_banner.privacy_policy.link")}
          </a>
          {t("gdpr.cookie_banner.privacy_policy.suffix")}
        </p>
      </div>
      <div className="flex flex-row items-center gap-4">
        <Button onClick={() => handleClick(false)} variant="secondary">
          {t("gdpr.cookie_banner.decline_button")}
        </Button>
        <Button onClick={() => handleClick(true)} variant="primary">
          {t("gdpr.cookie_banner.accept_button")}
        </Button>
      </div>
    </div>
  );
};
